/***
 *
 *   AUTHENTICATION
 *   Auth provider to manage auth functions throughout
 *   the application. <PrivateRoute> component to
 *   protect internal application routes from unauthenticated
 *   access.
 *
 **********/

import { useState, useEffect } from "react";
import axios from "axios";
import { AuthContext, useAPI, Event } from "components/lib";
import { Navigate } from "react-router-dom";

// auth context
import permissions from "./permissions";

export function AuthProvider(props) {
  const cache = JSON.parse(localStorage.getItem("user"));
  // Only load token from cache, not email (avoid stale data)
  const [user, setUser] = useState(cache?.token ? { token: cache.token } : null);
  const [authLoading, setAuthLoading] = useState(!!cache?.token);

  // Development mode: auto-create mock user if none exists
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && !user) {
      const mockUser = {
        token: "mock-dev-token-123",
        email: "dev@test.com",
        name: "Dev User",
        permission: "user",
        verified: true,
        plan: "development",
        subscription: "active",
        locale: "en"
      };
      localStorage.setItem("user", JSON.stringify(mockUser));
      setUser(mockUser);
      setAuthLoading(false);
    }
  }, [user]);

  // Skip API auth check if user has mock token (dev mode)
  const skipAuthAPI = user?.token === "mock-dev-token-123";
  const auth = useAPI(!skipAuthAPI && user ? "/api/auth" : null);

  useEffect(() => {
    // Skip auth validation for mock dev users
    if (skipAuthAPI) {
      setAuthLoading(false);
      return;
    }

    // update the auth status
    if (!auth.loading && auth.data) {
      setAuthLoading(false);
      auth.data.authenticated ? update(auth.data) : signout();
    } else if (!auth.loading && !auth.data && user?.token) {
      setAuthLoading(false);
    }
  }, [auth, skipAuthAPI]);
  function signin(res) {
    if (res.data) {
      const darkMode = JSON.parse(localStorage.getItem("dark_mode"));
      // console.log(darkMode, "darkmode");
      // // Save user data to localStorage
      const userData = res.data;
      // console.log(darkMode !== null, "userData");

      // // Reattach dark_mode to user data if it exists
      if (darkMode !== null) {
        userData.dark_mode = darkMode;
        // console.log(darkMode, "dark moood");
      }
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      setAuthLoading(false);
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + res.data.token;
      Event({ name: "signin" });

      if (!res.data.verified) return "/signup/verify";

      // Skip plan selection - all users start on free plan
      return res.data.onboarded ? "/dashboard" : "/welcome";
    }
  }

  async function signout() {
    axios({ method: "delete", url: "/api/auth" });
    // Clear all user data from localStorage on signout
    localStorage.removeItem("user");
    setUser(null);
    setAuthLoading(false);
    return (window.location = "/signin");
  }

  async function switchAccount(id) {
    const res = await axios({
      method: "post",
      url: "/api/auth/switch",
      data: { account: id },
    });

    if (res.data) signin(res);
  }

  function update(data) {
    if (localStorage.getItem("user")) {
      let user = JSON.parse(localStorage.getItem("user"));
      for (let key in data) {
        if (Array.isArray(data[key])) {
          user[key] = data[key];
        } else if (typeof data[key] === "object") {
          for (let innerKey in data[key]) {
            user[key][innerKey] = data[key][innerKey];
          }
        } else {
          user[key] = data[key];
        }
      }

      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
    }
  }
  // console.log(user, "user");
  return (
    <AuthContext.Provider
      value={{
        user: user,
        loading: authLoading,
        signin: signin,
        signout: signout,
        update: update,
        switchAccount: switchAccount,
        permission: permissions[user?.permission],
      }}
      {...props}
    />
  );
}

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute(props) {
  // check user exists
  const user = JSON.parse(localStorage.getItem("user"));
  const path = window.location.pathname;
  const permittedRoutes = [
    "https://billing.stripe.com/p/login/dRm5kE5VwdiReMC9AI93y00",
    "/signup/plan",
    "/account/upgrade",
    "/account",
    "/account/profile",
  ];

  if (user?.token) {
    if (permissions[user.permission][props.permission]) {
      if (user.verified) {
        // All users now start on free plan automatically - no redirect needed

        // user has no subscription
        // if (
        //   user.subscription !== "active" &&
        //   user.subscription !== "trialing" &&
        //   user.permission !== "master" &&
        //   !permittedRoutes.includes(path)
        // )
        //   return <Navigate to="https://billing.stripe.com/p/login/dRm5kE5VwdiReMC9AI93y00" />;

        // hide verified view if user is verified
        if (path === "/signup/verify") return <Navigate to="/dashboard" />;
      } else {
        // user is not verified
        if (path !== "/account/profile" && path !== "/signup/verify")
          return <Navigate to="/signup/verify" />;
      }

      // user is good
      return props.children;
    }
  }
  return <Navigate to="/signin" />;
}
